import React, { useState, useEffect } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import PageHeader from '../../Common/PageHeader';
import Breadcrumbs from '../../Common/Breadcrumbs';
import { DeleteModal } from '../../Common/DeleteModal';
import TournamentList from './components/TournamentList';
import { useNavigate } from 'react-router-dom';
import { listAdminTournaments, deleteTournament, uploadTournamentLogo, addTournament } from '../../api/tournament/tournamentService';
import Select from 'react-select';
import { getImageUrl } from '../../utils/imageUrl';

// Define the type for sport option
interface SportOption {
  value: string;
  label: string;
}

// Helper to format date for input fields
function formatDateForInput(dateStr: string) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toISOString().slice(0, 10);
}

const TournamentListPage: React.FC = () => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [form, setForm] = useState<{
    name: string;
    shortName: string;
    location: { value: string; label: string } | null;
    sport: SportOption | null;
    startDate: string;
    endDate: string;
    logo: string;
    status: { value: string; label: string } | null;
  }>(
    {
      name: '',
      shortName: '',
      location: null,
      sport: null,
      startDate: '',
      endDate: '',
      logo: '',
      status: null,
    }
  );
  const [formTouched, setFormTouched] = useState<any>({});
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string, name: string, sport: string } | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [sportFilter, setSportFilter] = useState<SportOption | null>(null);
  const [locationFilter, setLocationFilter] = useState<{ value: string; label: string } | null>(null);
  const [statusFilter, setStatusFilter] = useState<{ value: string; label: string } | null>(null);

  const sportOptions: SportOption[] = [
    { value: 'Football', label: 'Football' },
    { value: 'Tennis', label: 'Tennis' },
    { value: 'Pickleball', label: 'Pickleball' },
  ];

  const locationOptions = [
    { value: 'Mumbai', label: 'Mumbai' },
    { value: 'Navi Mumbai', label: 'Navi Mumbai' },
    { value: 'Pune', label: 'Pune' },
    { value: 'Thane', label: 'Thane' },
  ];

  const statusOptions = [
    { value: 'ongoing', label: 'Ongoing' },
    { value: 'completed', label: 'Completed' },
    { value: 'upcoming', label: 'Upcoming' },
  ];

  useEffect(() => {
    setLoading(true);
    setApiError(null);
    listAdminTournaments(page, pageSize)
      .then(data => {
        // Map '_id' to 'id' and 'sports' to 'sport' for TournamentList compatibility
        const mappedTournaments = (data?.paginatedResults || []).map((t: any) => ({
          ...t,
          id: t._id,
          sport: t.sports,
        }));
        setTournaments(mappedTournaments);
        setTotalPages(data?.totalPages || 1);
        setTotalCount(data?.totalCount || 0);
        setLoading(false);
      })
      .catch(() => {
        setApiError('Failed to load tournaments.');
        setLoading(false);
      });
  }, [page, pageSize]);

  // Filtering logic
  const filteredTournaments = tournaments.filter(t => {
    const matchesSearch =
      (t.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.shortName || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.location || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.sport || '').toLowerCase().includes(search.toLowerCase());
    const matchesSport = !sportFilter || (t.sport && t.sport.toLowerCase() === sportFilter.value.toLowerCase());
    const matchesLocation = !locationFilter || (t.location && t.location.toLowerCase() === locationFilter.value.toLowerCase());
    const matchesStatus = !statusFilter || (t.status && t.status.toLowerCase() === statusFilter.value.toLowerCase());
    return matchesSearch && matchesSport && matchesLocation && matchesStatus;
  });

  const handleEdit = (idx: number) => {
    const tournament = filteredTournaments[idx];
    navigate(`/tournament-details/${tournament._id || tournament.id}`);
  };

  const handleDelete = (idx: number) => {
    setDeleteTarget({ id: tournaments[idx]._id, name: tournaments[idx].name, sport: tournaments[idx].sports || tournaments[idx].sport });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteTarget) {
      const id = String(deleteTarget.id);
      const prev = tournaments;
      setTournaments(tournaments.filter(t => String(t._id) !== id));
      setShowDeleteModal(false);
      setDeleteTarget(null);
      try {
        await deleteTournament(id, deleteTarget.sport);
      } catch (err) {
        setTournaments(prev);
        setApiError('Failed to delete tournament.');
      }
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const validateForm = () => {
    return {
      name: !form.name.trim() ? 'Name is required.' : '',
      shortName: !form.shortName.trim() ? 'Abbreviation is required.' : '',
      location: !form.location ? 'Location is required.' : '',
      sport: !form.sport ? 'Sport is required.' : '',
      startDate: !form.startDate ? 'Start Date is required.' : '',
      endDate: !form.endDate ? 'End Date is required.' : '',
      status: !form.status ? 'Status is required.' : '',
    };
  };
  const errors = validateForm();
  const isFormValid = Object.values(errors).every(x => !x);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormTouched({ ...formTouched, [e.target.name]: true });
  };
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormTouched({ ...formTouched, [e.target.name]: true });
  };
  const handleSportChange = (option: SportOption | null) => {
    setForm({ ...form, sport: option });
    setFormTouched({ ...formTouched, sport: true });
  };

  const handleLocationChange = (option: { value: string; label: string } | null) => {
    setForm({ ...form, location: option });
    setFormTouched({ ...formTouched, location: true });
  };

  const handleStatusChange = (option: any) => {
    setForm({ ...form, status: option });
    setFormTouched({ ...formTouched, status: true });
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    setLogoError(null);
    try {
      const s3Key = await uploadTournamentLogo(file);
      setForm(prev => ({ ...prev, logo: s3Key }));
    } catch (err) {
      setLogoError('Failed to upload logo.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSaveTournament = async () => {
    setFormTouched({
      name: true,
      shortName: true,
      location: true,
      sport: true,
      startDate: true,
      endDate: true,
      status: true,
    });
    if (!isFormValid) return;
    try {
      if (editIndex === null) {
        await addTournament({
          name: form.name,
          shortName: form.shortName,
          logo: form.logo,
          location: form.location ? form.location.value : '',
          startDate: form.startDate,
          endDate: form.endDate,
          status: form.status ? (typeof form.status === 'string' ? form.status : form.status.value) : '',
          sports: form.sport ? form.sport.value : '',
          sortNo: 0
        });
      } else {
        // TODO: Implement edit logic if needed
      }
      // Refresh the list
      const data = await listAdminTournaments(page, pageSize);
      setTournaments((data?.paginatedResults || []).map((t: any) => ({ ...t, id: t._id, sport: t.sports, status: t.status })));
      setTotalPages(data?.totalPages || 1);
      setShowModal(false);
      setForm({ name: '', shortName: '', location: null, sport: null, startDate: '', endDate: '', logo: '', status: null });
      setEditIndex(null);
      setFormTouched({});
    } catch (err) {
      setFormError('Failed to add tournament.');
    }
  };

  return (
    <div className="container-fluid tournament-details-page" style={{ marginTop: '20px' }}>
      {/* Breadcrumbs - Outside flex container to ensure visibility */}
      <div className="mb-3 breadcrumbs-container" style={{ 
        padding: '0 15px', 
        position: 'relative', 
        zIndex: 10, 
        marginTop: '0', 
        marginBottom: '16px',
        background: 'transparent'
      }}>
        <Breadcrumbs items={[{ label: 'Tournaments', active: true }]} />
      </div>
      
      <div className="d-flex" style={{ minHeight: '100vh' }}>
        <div style={{ flex: 1 }}>
          <PageHeader title="Tournaments" breadcrumb={<></>} />
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              <span>Loading tournaments...</span>
            </div>
          ) : apiError ? (
            <div className="alert alert-danger">{apiError}</div>
          ) : (
            <>
              <TournamentList
                tournaments={filteredTournaments}
                search={search}
                onSearch={setSearch}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAdd={() => setShowModal(true)}
                sportFilter={sportFilter}
                setSportFilter={setSportFilter}
                sportOptions={sportOptions}
                locationFilter={locationFilter}
                setLocationFilter={setLocationFilter}
                locationOptions={locationOptions}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                statusOptions={statusOptions}
              />
              {/* Pagination UI - WeeklyReports style */}
              {!loading && !apiError && totalPages > 1 && (
                <div className="d-flex pagination-row align-items-center mt-3"
                  style={{
                    width: 1160,
                    height: 52,
                    justifyContent: 'space-between',
                    borderRadius: '8px',
                    paddingTop: 8,
                    paddingRight: 12,
                    paddingBottom: 8,
                    paddingLeft: 12,
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'Helvetica Neue, Arial, sans-serif',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '16px',
                      letterSpacing: 0,
                    }}
                  >
                    Showing results
                    <select
                      value={pageSize}
                      onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                      className="mx-2"
                      style={{
                        width: 70,
                        height: 36,
                        borderRadius: 8,
                        padding: 8,
                        gap: 8,
                        borderWidth: 0.5,
                        borderStyle: 'solid',
                        borderColor: '#888',
                        fontFamily: 'Helvetica Neue, Arial, sans-serif',
                        fontSize: '12px',
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                    of {totalCount}
                  </div>
                  <div className="d-flex align-items-center">
                    Page
                    <button className="btn btn-light mx-1" onClick={() => setPage(page - 1)} disabled={page === 1}>&lt;</button>
                    <span className="btn btn-primary mx-1 disabled">{page}</span>
                    {Array.from({ length: Math.min(3, totalPages) }, (_, i) => i + 1 + page).map(p => (
                      p <= totalPages ? (
                        <button key={p} className="btn btn-light mx-1" onClick={() => setPage(p)}>{p}</button>
                      ) : null
                    ))}
                    {page < totalPages - 2 && <span className="mx-1">...</span>}
                    <button className="btn btn-light mx-1" onClick={() => setPage(page + 1)} disabled={page === totalPages}>&gt;</button>
                    <span className="mx-2">of {totalPages}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{editIndex !== null ? 'Edit Tournament' : 'Add Tournament'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <div className="alert alert-danger">{formError}</div>}
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name<span style={{ color: 'red' }}>*</span></Form.Label>
                <Form.Control name="name" value={form.name} onChange={handleInputChange} isInvalid={!!errors.name && formTouched.name} onBlur={() => setFormTouched({ ...formTouched, name: true })} />
                {errors.name && formTouched.name && <div className="text-danger">{errors.name}</div>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Abbreviation<span style={{ color: 'red' }}>*</span></Form.Label>
                <Form.Control name="shortName" value={form.shortName} onChange={handleInputChange} isInvalid={!!errors.shortName && formTouched.shortName} onBlur={() => setFormTouched({ ...formTouched, shortName: true })} />
                {errors.shortName && formTouched.shortName && <div className="text-danger">{errors.shortName}</div>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Location<span style={{ color: 'red' }}>*</span></Form.Label>
                <Select
                  classNamePrefix="react-select"
                  name="location"
                  value={form.location}
                  onChange={handleLocationChange}
                  options={locationOptions}
                  placeholder="Select Location"
                  isClearable={false}
                  styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                />
                {errors.location && formTouched.location && <div className="text-danger">{errors.location}</div>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Sport<span style={{ color: 'red' }}>*</span></Form.Label>
                <Select
                  classNamePrefix="react-select"
                  name="sport"
                  value={form.sport}
                  onChange={handleSportChange}
                  options={sportOptions}
                  placeholder="Select Sport"
                  isClearable={false}
                  styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                />
                {errors.sport && formTouched.sport && <div className="text-danger">{errors.sport}</div>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Start Date<span style={{ color: 'red' }}>*</span></Form.Label>
                <Form.Control type="date" name="startDate" value={form.startDate} onChange={handleInputChange} isInvalid={!!errors.startDate && formTouched.startDate} onBlur={() => setFormTouched({ ...formTouched, startDate: true })} />
                {errors.startDate && formTouched.startDate && <div className="text-danger">{errors.startDate}</div>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>End Date<span style={{ color: 'red' }}>*</span></Form.Label>
                <Form.Control type="date" name="endDate" value={form.endDate} onChange={handleInputChange} isInvalid={!!errors.endDate && formTouched.endDate} onBlur={() => setFormTouched({ ...formTouched, endDate: true })} />
                {errors.endDate && formTouched.endDate && <div className="text-danger">{errors.endDate}</div>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Status<span style={{ color: 'red' }}>*</span></Form.Label>
                <Select
                  classNamePrefix="react-select"
                  name="status"
                  value={form.status}
                  onChange={handleStatusChange}
                  options={statusOptions as any}
                  placeholder="Select Status"
                  isClearable={false}
                  styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                />
                {errors.status && formTouched.status && <div className="text-danger">{errors.status}</div>}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Logo</Form.Label>
                <Form.Control type="file" accept="image/*" onChange={handleLogoChange} disabled={logoUploading} />
                {logoUploading && <div>Uploading...</div>}
                {logoError && <div className="text-danger">{logoError}</div>}
                {form.logo && <img src={getImageUrl(form.logo)} alt="Logo Preview" style={{ width: 60, marginTop: 8 }} />}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSaveTournament} disabled={!isFormValid || logoUploading}>{editIndex !== null ? 'Update' : 'Add'}</Button>
          </Modal.Footer>
        </Modal>
        <DeleteModal
          show={showDeleteModal}
          handleClose={closeDeleteModal}
          deleteModalFunction={confirmDelete}
          title="Delete Tournament"
          message={`Are you sure you want to delete '${deleteTarget?.name}'?`}
        />
    </div>
  );
};

export default TournamentListPage; 